import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import {
  UNITS,
  isInternalBarcode,
  unitLabel,
  type ProductListEntry,
  type Unit,
} from '@almox/shared';
import { api } from '../api/client.js';
import { useAppData } from '../AppData.js';
import { CategoryDatalist, EmptyState, storageEmoji, useAsync, useToast } from '../components/ui.js';

/** Unidades que fazem sentido para o que se compra a granel, na frente. */
const UNIDADES_GRANEL: Unit[] = ['kg', 'g', 'un', 'dz', 'pct', 'cx'];

/**
 * Entrada rapida de hortifruti e granel: o que nao tem codigo de barras para
 * escanear (cebola, alho, tomate, carne no peso).
 *
 * O fluxo e o oposto do scanner: comeca pelo nome. Produto ja comprado antes
 * aparece na busca e vai em tres toques; produto novo nasce aqui mesmo, com o
 * codigo interno que o Almox gera (regra 4) para poder ser reencontrado depois.
 */
export function QuickEntryPage() {
  const { storages, settings, categories, refresh } = useAppData();
  const toast = useToast();
  const [termo, setTermo] = useState('');
  const [busca, setBusca] = useState('');
  const encontrados = useAsync(() => api.listProducts(busca), [busca]);
  const [escolhido, setEscolhido] = useState<ProductListEntry | null>(null);
  const [novo, setNovo] = useState(false);

  // Sem busca, a lista vira "o que voce costuma comprar assim": produtos sem
  // codigo de barras de verdade, que sao justamente os de granel.
  const habituais = useMemo(
    () =>
      (encontrados.data ?? [])
        .filter((produto) => !produto.barcode || isInternalBarcode(produto.barcode))
        .slice(0, 12),
    [encontrados.data],
  );

  function buscar(evento: FormEvent) {
    evento.preventDefault();
    setBusca(termo.trim());
    setNovo(false);
  }

  async function aoGuardar() {
    setEscolhido(null);
    setNovo(false);
    setTermo('');
    setBusca('');
    encontrados.reload();
    await refresh();
  }

  if (storages.length === 0) {
    return (
      <main className="page">
        <EmptyState
          icon="🏠"
          title="Crie uma dispensa primeiro"
          hint="A entrada precisa de um lugar para guardar."
        />
        <Link to="/configuracoes" className="btn">
          Configuracoes
        </Link>
      </main>
    );
  }

  return (
    <main className="page">
      <div className="page-head">
        <h1>🥬 Entrada sem codigo</h1>
      </div>
      <p className="muted">
        Para o que nao tem codigo de barras: hortifruti, granel, feira. Informe o peso ou a
        quantidade comprada — o Almox gera um codigo interno para reencontrar o produto depois.
      </p>

      <form className="row" style={{ alignItems: 'flex-end' }} onSubmit={buscar}>
        <div className="field" style={{ marginBottom: 0, flex: '3 1 220px' }}>
          <label htmlFor="granel-busca">Produto</label>
          <input
            id="granel-busca"
            value={termo}
            onChange={(evento) => setTermo(evento.target.value)}
            placeholder="Ex.: cebola, alho, tomate"
            autoFocus
          />
        </div>
        <button type="submit" className="btn" style={{ flex: '0 0 auto' }}>
          Buscar
        </button>
      </form>

      {escolhido ? (
        <FormularioDeEntrada
          produto={escolhido}
          onCancel={() => setEscolhido(null)}
          onSaved={aoGuardar}
        />
      ) : novo ? (
        <FormularioDeEntrada
          nomeSugerido={termo.trim() || busca}
          categorias={categories}
          onCancel={() => setNovo(false)}
          onSaved={aoGuardar}
        />
      ) : (
        <>
          <section className="card">
            <h2>{busca ? `Resultados para "${busca}"` : 'O que voce ja comprou assim'}</h2>
            {encontrados.loading ? <p className="muted">Carregando…</p> : null}
            {!encontrados.loading && habituais.length === 0 ? (
              <p className="small muted">
                {busca
                  ? 'Nenhum produto encontrado com esse nome.'
                  : 'Nada ainda — cadastre o primeiro abaixo.'}
              </p>
            ) : null}
            <div className="pick-list">
              {habituais.map((produto) => (
                <button
                  key={produto.id}
                  type="button"
                  className="btn btn-block btn-pick"
                  onClick={() => setEscolhido(produto)}
                >
                  <span>
                    {produto.name}
                    {produto.brand ? <span className="small muted"> · {produto.brand}</span> : null}
                  </span>
                  <span className="small muted">
                    {produto.itemCount > 0
                      ? `${produto.totalQuantity} ${produto.unit} em casa`
                      : produto.unit}
                  </span>
                </button>
              ))}
            </div>
          </section>

          <button
            type="button"
            className="btn btn-primary btn-block"
            onClick={() => setNovo(true)}
          >
            ➕ Cadastrar {termo.trim() ? `"${termo.trim()}"` : 'um produto novo'}
          </button>
        </>
      )}

      <p className="small muted" style={{ marginTop: 16 }}>
        Tem codigo de barras? <Link to="/escanear">Use o scanner</Link> — e mais rapido.
      </p>
      {settings.lastStorageId ? null : (
        <p className="small muted">A dispensa usada fica lembrada para a proxima entrada.</p>
      )}
    </main>
  );
}

/**
 * Peso/quantidade + dispensa. Com produto escolhido, so isso; com produto novo,
 * o cadastro minimo (nome, unidade, categoria) vem junto.
 */
function FormularioDeEntrada({
  produto,
  nomeSugerido,
  categorias,
  onCancel,
  onSaved,
}: {
  produto?: ProductListEntry;
  nomeSugerido?: string;
  categorias?: string[];
  onCancel: () => void;
  onSaved: () => Promise<void>;
}) {
  const { storages, settings } = useAppData();
  const toast = useToast();
  const [nome, setNome] = useState(nomeSugerido ?? '');
  const [categoria, setCategoria] = useState('Hortifruti');
  const [unidade, setUnidade] = useState<Unit>((produto?.unit as Unit) ?? 'kg');
  const [quantidade, setQuantidade] = useState('1');
  const [storageId, setStorageId] = useState(settings.lastStorageId ?? storages[0]?.id ?? '');
  const [validade, setValidade] = useState('');
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    if (produto) setUnidade(produto.unit as Unit);
  }, [produto]);

  async function guardar(evento: FormEvent) {
    evento.preventDefault();
    const peso = Number(quantidade.replace(',', '.'));
    if (!Number.isFinite(peso) || peso <= 0) {
      toast('Informe o peso ou a quantidade comprada.', 'error');
      return;
    }
    if (!produto && !nome.trim()) {
      toast('Informe o nome do produto.', 'error');
      return;
    }

    setSalvando(true);
    try {
      const item = await api.createEntry({
        ...(produto
          ? { productId: produto.id }
          : {
              product: {
                productType: nome.trim(),
                unit: unidade,
                ...(categoria.trim() ? { category: categoria.trim() } : {}),
              },
            }),
        storageId,
        quantity: peso,
        ...(validade ? { expiresAt: validade } : {}),
        restockOnDepletion: true,
      });
      toast(
        `${item.product.name}: ${item.quantity} ${item.product.unit} em ${item.storage.name}.`,
        'ok',
      );
      await onSaved();
    } catch (error) {
      toast((error as Error).message, 'error');
    } finally {
      setSalvando(false);
    }
  }

  return (
    <form className="card" onSubmit={guardar}>
      <h2>{produto ? produto.name : 'Produto novo'}</h2>

      {produto ? null : (
        <>
          <div className="field">
            <label htmlFor="granel-nome">Nome</label>
            <input
              id="granel-nome"
              value={nome}
              onChange={(evento) => setNome(evento.target.value)}
              placeholder="Ex.: Cebola"
              required
            />
          </div>
          <div className="field">
            <label htmlFor="granel-categoria">Categoria</label>
            <input
              id="granel-categoria"
              value={categoria}
              onChange={(evento) => setCategoria(evento.target.value)}
              list="categorias"
            />
            <CategoryDatalist categories={categorias ?? []} />
            <span className="small muted">Define a validade estimada (Hortifruti: 7 dias).</span>
          </div>
        </>
      )}

      <div className="row">
        <div className="field">
          <label htmlFor="granel-qtd">Peso / quantidade comprada</label>
          <input
            id="granel-qtd"
            value={quantidade}
            onChange={(evento) => setQuantidade(evento.target.value)}
            inputMode="decimal"
            placeholder="Ex.: 1,250"
            required
          />
          <span className="small muted">Aceita virgula: 1,250 kg.</span>
        </div>
        <div className="field">
          <label htmlFor="granel-unidade">Unidade</label>
          <select
            id="granel-unidade"
            value={unidade}
            onChange={(evento) => setUnidade(evento.target.value as Unit)}
            disabled={Boolean(produto)}
          >
            {[...UNIDADES_GRANEL, ...UNITS.filter((u) => !UNIDADES_GRANEL.includes(u))].map(
              (opcao) => (
                <option key={opcao} value={opcao}>
                  {unitLabel(opcao)}
                </option>
              ),
            )}
          </select>
          {produto ? (
            <span className="small muted">
              A unidade vem do catalogo — mude em Produtos, se estiver errada.
            </span>
          ) : null}
        </div>
      </div>

      <div className="row">
        <div className="field">
          <label htmlFor="granel-dispensa">Dispensa</label>
          <select
            id="granel-dispensa"
            value={storageId}
            onChange={(evento) => setStorageId(evento.target.value)}
            required
          >
            {storages.map((storage) => (
              <option key={storage.id} value={storage.id}>
                {storageEmoji(storage.icon)} {storage.name}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="granel-validade">Validade (opcional)</label>
          <input
            id="granel-validade"
            type="date"
            value={validade}
            onChange={(evento) => setValidade(evento.target.value)}
          />
        </div>
      </div>

      <div className="modal-actions">
        <button type="button" className="btn" onClick={onCancel}>
          Cancelar
        </button>
        <button type="submit" className="btn btn-primary" disabled={salvando || !storageId}>
          {salvando ? 'Guardando…' : 'Guardar'}
        </button>
      </div>
    </form>
  );
}
